// authSync.js
// Synchronizes Google OAuth with Firebase Auth using real Firebase credentials

import { signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase';

/**
 * Sign in to Firebase using a Google access token (and optional ID token).
 * @param {string} accessToken - Google OAuth access token
 * @param {string|null} idToken - Google ID token, if available
 * @returns {Promise<import('firebase/auth').User>} Firebase user object
 */
export const syncGoogleAuthWithFirebase = async (accessToken, idToken = null) => {
  const credential = GoogleAuthProvider.credential(idToken, accessToken);
  const result = await signInWithCredential(auth, credential);
  console.log('Firebase Auth sign-in succeeded for:', result.user?.email);
  return result.user;
};

/**
 * Restore Firebase auth state using a cached Google access token.
 * Falls back silently if the token is missing or invalid.
 */
export const restoreFirebaseAuthFromGoogle = async () => {
  const storedToken = (() => {
    try {
      return localStorage.getItem('google_access_token');
    } catch {
      return null;
    }
  })();

  if (!storedToken) {
    console.log('No stored Google access token found');
    return null;
  }

  try {
    return await syncGoogleAuthWithFirebase(storedToken);
  } catch (error) {
    const errorCode = String(error?.code || '').toLowerCase();
    const errorMessage = String(error?.message || '').toLowerCase();
    const shouldClearToken =
      errorCode.includes('invalid') ||
      errorCode.includes('credential') ||
      errorCode.includes('unauthorized') ||
      errorCode.includes('token-expired') ||
      errorMessage.includes('invalid credential') ||
      errorMessage.includes('token has expired');

    if (shouldClearToken) {
      console.warn('Stored Google token is invalid; clearing cached token.', error);
      try { localStorage.removeItem('google_access_token'); } catch {}
    } else {
      console.warn('Stored Google token restore failed due to a transient error; keeping cached token.', error);
    }
    return null;
  }
};

/**
 * Enhanced sign-in wrapper to ensure Google and Firebase stay in sync.
 * @param {Function} googleSignInFunction - Function that returns { accessToken, idToken? }
 */
export const enhancedGoogleSignIn = async (googleSignInFunction) => {
  const googleResult = await googleSignInFunction();
  const firebaseUser = await syncGoogleAuthWithFirebase(
    googleResult.accessToken,
    googleResult.idToken || null
  );

  return {
    google: googleResult,
    firebase: firebaseUser,
    success: true
  };
};
